import { useQueryClient } from '@tanstack/react-query';
import { PanelLeft, Search, ShieldCheck, SlidersHorizontal } from 'lucide-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { qk } from '../lib/keys';
import { useSession } from '../lib/queries';
import { ui } from '../lib/store';
import { Logo } from './ui';

/** "Search ⌘K" trigger used in the top bar. */
export function SearchButton() {
  return (
    <button className="btn btn-secondary" onClick={() => ui.set({ palette: true })}>
      <Search className="icon" style={{ fontSize: 15 }} />
      Search
      <span className="kbd">⌘K</span>
    </button>
  );
}

/** 42px avatar on the dark ink with a white ring; clicking signs out. */
export function UserAvatar() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const user = useSession().data?.user;
  const signOut = async () => {
    await api.post('/api/auth/logout');
    qc.clear();
    await qc.invalidateQueries({ queryKey: qk.session });
    nav('/login');
  };
  return (
    <button className="avatar ring" title={`Sign out ${user?.email ?? ''}`} aria-label="Sign out" onClick={signOut}>
      {user?.initials ?? '··'}
    </button>
  );
}

/**
 * The 68px white app bar shared by every signed-in screen:
 * [sidebar toggle] logo | AI Harness | <left> ……… <right> [settings] [avatar]
 */
export default function TopBar({
  onToggleSidebar,
  left,
  right,
  settings = true,
  avatar = true,
}: {
  onToggleSidebar?: () => void;
  left?: ReactNode;
  right?: ReactNode;
  settings?: boolean | string;
  avatar?: boolean;
}) {
  const nav = useNavigate();
  const isAdmin = !!useSession().data?.user?.isAdmin;
  return (
    <header className="topbar">
      {onToggleSidebar && (
        <button className="btn btn-icon btn-secondary" onClick={onToggleSidebar} title="Toggle sidebar" aria-label="Toggle sidebar">
          <PanelLeft className="icon" style={{ fontSize: 17 }} />
        </button>
      )}
      <button className="topbar-brand" onClick={() => nav('/')} aria-label="AI Harness — all projects">
        <Logo />
        <span className="topbar-sep" />
        <span className="topbar-name">AI Harness</span>
      </button>
      {left && (
        <>
          <span className="topbar-sep wide" />
          {left}
        </>
      )}
      <span style={{ flex: 1 }} />
      {right}
      {isAdmin && (
        <button className="btn btn-icon btn-secondary topbar-end" title="Admin: people, keys and the agent library" aria-label="Admin" onClick={() => nav('/admin/people')}>
          <ShieldCheck className="icon" />
        </button>
      )}
      {settings && (
        <button className="btn btn-icon btn-secondary topbar-end" title="Settings" aria-label="Settings" onClick={() => nav(typeof settings === 'string' ? settings : '/settings/providers')}>
          <SlidersHorizontal className="icon" />
        </button>
      )}
      {avatar && <UserAvatar />}
    </header>
  );
}

